import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, CalendarDays, Clock, MapPin, User, Phone, 
  MessageCircle, Send, Loader2, CheckCircle2, Image as ImageIcon,
  ChevronDown, ChevronUp, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ status }) {
  const statusConfig = {
    draft: { color: 'bg-slate-500', label: 'Scheduled' },
    confirmed: { color: 'bg-blue-500', label: 'Confirmed' },
    assigned: { color: 'bg-purple-500', label: 'Assigned' },
    accepted: { color: 'bg-indigo-500', label: 'Ready' },
    in_progress: { color: 'bg-amber-500', label: 'In Progress' },
    completed: { color: 'bg-green-500', label: 'Completed' },
    cancelled: { color: 'bg-red-500', label: 'Cancelled' },
  };

  const config = statusConfig[status] || { color: 'bg-slate-500', label: status };

  return (
    <span className={`${config.color} text-white text-sm font-medium px-3 py-1.5 rounded-full`}>
      {config.label}
    </span>
  );
}

function ChecklistSummary({ checklist }) {
  const [expanded, setExpanded] = useState(false);

  if (!checklist || checklist.length === 0) return null;

  // Group by category
  const grouped = checklist.reduce((acc, item) => {
    const cat = item.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const completedCount = checklist.filter(i => i.status === 'done').length;

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-400" />
          <div>
            <h3 className="text-white font-medium">Checklist</h3>
            <p className="text-sm text-slate-400">
              {completedCount} of {checklist.length} tasks completed
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-slate-700/50 p-4 space-y-4">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category}>
              <h4 className="text-sm font-medium text-slate-400 mb-2">{category}</h4>
              <div className="space-y-2">
                {items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    {item.status === 'done' ? (
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 border-slate-600 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${item.status === 'done' ? 'text-slate-300' : 'text-slate-500'}`}>
                      {item.name}
                    </span>
                    {item.has_photos && (
                      <ImageIcon className="w-3.5 h-3.5 text-slate-500" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoGallery({ photos }) {
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  if (!photos || photos.length === 0) return null;

  const beforePhotos = photos.filter(p => p.photo_type === 'before');
  const afterPhotos = photos.filter(p => p.photo_type === 'after');

  const PhotoSection = ({ title, items }) => {
    if (items.length === 0) return null;
    return (
      <div>
        <h4 className="text-sm font-medium text-slate-400 mb-2">{title}</h4>
        <div className="grid grid-cols-3 gap-2">
          {items.map(photo => (
            <button
              key={photo.id}
              onClick={() => setSelectedPhoto(photo)}
              className="aspect-square rounded-lg overflow-hidden bg-slate-700 hover:ring-2 hover:ring-amber-500 transition-all"
            >
              <img
                src={`${API_URL}${photo.url}`}
                alt={photo.caption || title}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4 space-y-4">
        <div className="flex items-center gap-3">
          <ImageIcon className="w-5 h-5 text-amber-400" />
          <h3 className="text-white font-medium">Photos</h3>
        </div>
        <PhotoSection title="Before" items={beforePhotos} />
        <PhotoSection title="After" items={afterPhotos} />
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <img
            src={`${API_URL}${selectedPhoto.url}`}
            alt={selectedPhoto.caption || 'Photo'}
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}
    </>
  );
}

function MessageThread({ comments, onSendMessage, sending }) {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    onSendMessage(message);
    setMessage('');
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
      <div className="p-4 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-5 h-5 text-amber-400" />
          <h3 className="text-white font-medium">Messages</h3>
        </div>
      </div>

      {/* Messages */}
      <div className="h-64 overflow-y-auto p-4 space-y-3">
        {(!comments || comments.length === 0) ? (
          <div className="text-center text-slate-500 py-8">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No messages yet</p>
            <p className="text-xs mt-1">Send a message about this booking</p>
          </div>
        ) : (
          comments.map(comment => {
            const isClient = comment.user_role === 'client';
            return (
              <div
                key={comment.id}
                className={`flex ${isClient ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    isClient
                      ? 'bg-amber-500 text-white rounded-br-sm'
                      : 'bg-slate-700 text-slate-200 rounded-bl-sm'
                  }`}
                >
                  {!isClient && (
                    <div className="text-xs text-slate-400 mb-1">{comment.user_name}</div>
                  )}
                  <p className="text-sm">{comment.message}</p>
                  <div className={`text-xs mt-1 ${isClient ? 'text-amber-200' : 'text-slate-500'}`}>
                    {format(parseISO(comment.created_at), 'MMM d, h:mm a')}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-3 border-t border-slate-700/50">
        <div className="flex gap-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
            data-testid="message-input"
          />
          <button
            type="submit"
            disabled={sending || !message.trim()}
            className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-xl transition-colors"
            data-testid="send-message-btn"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function ClientPortalJobDetail() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [sending, setSending] = useState(false);

  const getToken = () => localStorage.getItem('portal_token');

  const fetchBooking = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate('/portal/login');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/client-portal/booking/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_user');
        navigate('/portal/login');
        return;
      }

      if (!res.ok) throw new Error('Failed to load booking');
      const data = await res.json();
      setBooking(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [bookingId, navigate]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  const sendMessage = async (message) => {
    const token = getToken();
    if (!token) return;

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/client-portal/booking/${bookingId}/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ message })
      });

      if (!res.ok) throw new Error('Failed to send message');
      
      toast.success('Message sent');
      fetchBooking(); // Refresh to get updated comments
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-400">Booking not found</p>
          <button
            onClick={() => navigate('/portal')}
            className="mt-4 text-amber-400 hover:text-amber-300"
          >
            Go back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const scheduledDate = parseISO(booking.scheduled_date);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/portal')}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-white">Booking Details</h1>
              <p className="text-sm text-slate-400">
                {format(scheduledDate, 'EEEE, MMMM d, yyyy')}
              </p>
            </div>
            <StatusBadge status={booking.status} />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Service Details */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4 space-y-4">
          {/* Date & Time */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <div className="text-white font-medium">
                {format(scheduledDate, 'EEEE, MMMM d, yyyy')}
              </div>
              {booking.time_window_start && (
                <div className="text-sm text-slate-400">
                  {booking.time_window_start}
                  {booking.time_window_end && ` - ${booking.time_window_end}`}
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="text-white font-medium">Service Address</div>
              <div className="text-sm text-slate-400">{booking.service_address}</div>
            </div>
          </div>

          {/* Staff Info */}
          {booking.staff_info && booking.staff_info.length > 0 && (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <div className="text-white font-medium">Your Cleaner{booking.staff_info.length > 1 ? 's' : ''}</div>
                <div className="text-sm text-slate-400">
                  {booking.staff_info.map((s, i) => (
                    <div key={i}>{s.name}</div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Job Details */}
          {booking.job_details && (
            <div className="pt-3 border-t border-slate-700/50">
              <div className="text-sm text-slate-400 mb-1">Service Notes</div>
              <div className="text-white text-sm">{booking.job_details}</div>
            </div>
          )}

          {/* Time on site (if completed) */}
          {booking.status === 'completed' && booking.total_time_on_site_minutes && (
            <div className="pt-3 border-t border-slate-700/50 flex items-center gap-3">
              <Clock className="w-5 h-5 text-green-400" />
              <div>
                <div className="text-white font-medium">Time on Site</div>
                <div className="text-sm text-slate-400">
                  {Math.floor(booking.total_time_on_site_minutes / 60)}h {booking.total_time_on_site_minutes % 60}m
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Checklist Summary */}
        <ChecklistSummary checklist={booking.checklist_summary} />

        {/* Photos */}
        <PhotoGallery photos={booking.photos} />

        {/* Messages */}
        <MessageThread
          comments={booking.comments}
          onSendMessage={sendMessage}
          sending={sending}
        />
      </main>
    </div>
  );
}
